import { type ReactElement, useEffect, useState } from 'react';
import { Button } from '@/shared/ui';

interface PrintProductionOrderButtonProps {
  /** 인쇄 전용 화면 경로 — 보이지 않는 프레임에 띄워 그 안에서 인쇄창을 연다. */
  readonly href: string;
}

/** 인쇄 전용 화면이 인쇄창을 닫은 뒤 부모 창에 보내는 메시지 종류. */
export const PRODUCTION_ORDER_PRINTED_MESSAGE = 'production-order-printed';
/** 인쇄창이 끝내 열리지 않아도 프레임을 영영 두지 않는다. */
const PRINT_FRAME_TIMEOUT_MS = 120_000;
const FRAME_CLASS = 'pointer-events-none fixed top-0 left-0 h-0 w-0 opacity-0';

/**
 * 인쇄 / PDF 저장 버튼 — 새 탭 없이 바로 인쇄창을 연다. 누르면 인쇄 전용 화면을 보이지 않는 프레임에 띄우고, 그 화면이 시안을 다
 * 그린 뒤 스스로 인쇄창을 연다. 인쇄창이 닫히면(`afterprint`) 화면이 보내는 메시지를 받아 프레임을 거둔다.
 */
export const PrintProductionOrderButton = ({
  href,
}: PrintProductionOrderButtonProps): ReactElement => {
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!printing) return;
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      const data: unknown = event.data;
      if (
        typeof data === 'object' &&
        data !== null &&
        'type' in data &&
        data.type === PRODUCTION_ORDER_PRINTED_MESSAGE
      )
        setPrinting(false);
    };
    window.addEventListener('message', onMessage);
    const timer = setTimeout(() => setPrinting(false), PRINT_FRAME_TIMEOUT_MS);
    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
    };
  }, [printing]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="md"
        isLoading={printing}
        disabled={printing}
        onClick={() => setPrinting(true)}
      >
        {printing ? '인쇄 준비 중…' : '인쇄 / PDF 저장'}
      </Button>
      {printing ? (
        <iframe src={href} title="발주서 인쇄" aria-hidden="true" className={FRAME_CLASS} />
      ) : null}
    </>
  );
};
